/**
 * Avvisi per i nuovi messaggi in arrivo su WhatsApp, Instagram e Messenger:
 * un suono discreto e, se l'utente l'ha permesso con il pulsante apposito,
 * una notifica del browser. Solo quando il drawer Messaggi e' chiuso o la
 * scheda del CRM non e' in primo piano. Montato nel WhatsAppDrawer, che resta
 * sempre nel DOM: gli store WhatsApp e Meta sono gia' aggiornati in realtime.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useMetaMessagingStore } from '@/lib/stores/metaMessagingStore';
import { useWhatsAppDrawerStore } from '@/lib/stores/whatsappDrawerStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { safeLocalStorage } from '@/components/whatsapp2/chat/rightPanelPreference';
import {
  canPlaySound, diffNewInbound, dropMuted, metaStamps, notificationText, readNotifyEnabled,
  shouldNotify, stampsToMap, whatsappStamps, writeNotifyEnabled, type InboundStamp,
} from './incomingNotify';
import { playSoftChime } from './chime';

export type BrowserPermission = 'default' | 'granted' | 'denied' | 'unsupported';

function currentPermission(): BrowserPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as BrowserPermission;
}

export function useIncomingMessageNotifications(userId: string | null | undefined) {
  const [enabled, setEnabledState] = useState(() => readNotifyEnabled(safeLocalStorage(), userId));
  const [permission, setPermission] = useState<BrowserPermission>(currentPermission);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => { setEnabledState(readNotifyEnabled(safeLocalStorage(), userId)); }, [userId]);

  const setEnabled = useCallback((value: boolean) => {
    setEnabledState(value);
    writeNotifyEnabled(safeLocalStorage(), userId, value);
  }, [userId]);

  /** Solo da un clic dell'utente: il browser mostra la sua richiesta di permesso. */
  const requestBrowserPermission = useCallback(async () => {
    if (currentPermission() === 'unsupported') return 'unsupported' as const;
    try {
      const res = await Notification.requestPermission();
      setPermission(res as BrowserPermission);
      return res as BrowserPermission;
    } catch {
      return currentPermission();
    }
  }, []);

  useEffect(() => {
    const since = Date.now();
    let waSeen = stampsToMap(whatsappStamps(useWhatsAppChatStore.getState().chats));
    let metaSeen = stampsToMap(metaStamps(useMetaMessagingStore.getState().conversations));
    let lastSound = 0;

    const notify = (all: InboundStamp[]) => {
      // Chat silenziate: nessun suono ne' notifica.
      const prefs = useWhatsAppChatMetaStore.getState().chatMeta;
      const fresh = dropMuted(all, (key) => prefs.get(key));
      if (fresh.length === 0) return;
      const drawerOpen = useWhatsAppDrawerStore.getState().isOpen;
      const documentHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      if (!shouldNotify({ enabled: enabledRef.current, drawerOpen, documentHidden })) return;
      const now = Date.now();
      if (canPlaySound(lastSound, now)) {
        lastSound = now;
        playSoftChime();
      }
      if (currentPermission() === 'granted') {
        try {
          const { title, body } = notificationText(fresh);
          const n = new Notification(title, { body, tag: 'flusia-messaggi', icon: '/favicon.ico' });
          n.onclick = () => {
            window.focus();
            const first = fresh[0];
            const id = first.key.slice(first.key.indexOf(':') + 1);
            const drawer = useWhatsAppDrawerStore.getState();
            if (first.channel === 'whatsapp') {
              drawer.openChannel('whatsapp');
              useWhatsAppChatStore.getState().selectChat(id);
            } else {
              drawer.openMetaConversation(first.channel, id);
            }
            n.close();
          };
        } catch { /* alcuni browser (mobile) non permettono new Notification */ }
      }
    };

    const unsubWa = useWhatsAppChatStore.subscribe((state, prev) => {
      if (state.chats === prev.chats) return;
      const stamps = whatsappStamps(state.chats);
      notify(diffNewInbound(waSeen, stamps, since));
      waSeen = new Map([...waSeen, ...stampsToMap(stamps)]);
    });
    const unsubMeta = useMetaMessagingStore.subscribe((state, prev) => {
      if (state.conversations === prev.conversations) return;
      const stamps = metaStamps(state.conversations);
      notify(diffNewInbound(metaSeen, stamps, since));
      metaSeen = new Map([...metaSeen, ...stampsToMap(stamps)]);
    });
    return () => { unsubWa(); unsubMeta(); };
  }, []);

  return { enabled, setEnabled, permission, requestBrowserPermission };
}
